import React, { useContext } from 'react';
import DisplacementSphere from '../components/background/DisplacementSphere';
import { ThemeToggle } from '../components/theme/ThemeToggle';
import { ThemeContext } from '../components/theme/ThemeProvider';
import { SocialIcons, ContactLinks } from '../components/content/SocialIcons';
import Resume from '../settings/resume.json';
import './Home.css';

const ExternalLink = ({ href, children, className }) => (
  <a href={href} className={className} target="_blank" rel="noopener noreferrer">
    {children} <span aria-hidden="true">↗</span>
  </a>
);

export const Home = () => {
  const { theme } = useContext(ThemeContext);
  const { basics, publications, research, projects, education, teaching, skills } = Resume;
  return (
    <div className={`portfolio portfolio--${theme}`} id="top">
      <DisplacementSphere />
      <a className="skip-link" href="#main">Skip to content</a>
      <header className="site-header page-width">
        <a className="wordmark" href="#top" aria-label="Ziheng Shangguan, home">
          <span className="monogram" aria-hidden="true">ZS<span>.</span></span>
          <span>Ziheng Shangguan</span>
        </a>
        <nav aria-label="Main navigation">
          <a href="#research">Research</a>
          <a href="#projects">Projects</a>
          <a href="#about">About</a>
          <ThemeToggle />
        </nav>
      </header>
      <main id="main">
        <section className="hero page-width" aria-labelledby="intro-title">
          <div className="hero-copy">
            <p className="eyebrow">{basics.institution} / Computer Science</p>
            <h1 id="intro-title">Hey, I'm <span>{basics.preferredName}.</span></h1>
            <p className="hero-name">Ziheng Shangguan · {basics.label}</p>
            <p className="hero-summary">{basics.summary}</p>
            <div className="hero-actions">
              <a href="#research" className="primary-link">Explore my research <span aria-hidden="true">↓</span></a>
              <a href="#contact">Get in touch <span aria-hidden="true">↘</span></a>
            </div>
          </div>
          <SocialIcons />
          <a className="latest-note" href="#paper-hmt">
            <span className="eyebrow">Latest work · August 2026</span>
            <span>Authenticated Data Structures<br />for Dynamic Workloads</span>
            <span className="note-caption">Huffman-Merkle Tree / arXiv preprint <span aria-hidden="true">↘</span></span>
          </a>
        </section>
        <div className="content-surface">
          <section className="section page-width" id="research" aria-labelledby="research-title">
            <div className="section-heading"><span className="section-number">01</span><h2 id="research-title">Research</h2></div>
            <p className="section-intro">Efficient storage and verification for workloads that change over time.</p>
            {publications.map(paper => (
              <article className="publication" id="paper-hmt" key={paper.name}>
                <div className="publication-meta"><span>{paper.year}</span><span className="status-label">{paper.publisher}</span></div>
                <div>
                  <h3><a href={paper.website} target="_blank" rel="noopener noreferrer">{paper.name}</a></h3>
                  <p className="authors"><strong>{paper.authors[0]}</strong>, {paper.authors.slice(1).join(', ')}</p>
                  <p className="body-copy">{paper.summary}</p>
                  <ul className="tags" aria-label="Research topics">{paper.tags.map(tag => <li key={tag}>{tag}</li>)}</ul>
                  <div className="paper-links"><ExternalLink href={paper.website}>Read on arXiv</ExternalLink><ExternalLink href={paper.pdf}>Paper PDF</ExternalLink><span>{paper.identifier}</span></div>
                </div>
              </article>
            ))}
            {research.map(item => (
              <article className="research-item" key={item.name}>
                <div><p className="eyebrow">{item.role}</p><h3>{item.name}</h3><p className="supervisors">Supervised by {item.supervisors}</p></div>
                <div><p className="date-label">{item.period}</p><p className="body-copy">{item.summary}</p></div>
              </article>
            ))}
          </section>
          <section className="section page-width" id="projects" aria-labelledby="projects-title">
            <div className="section-heading"><span className="section-number">02</span><h2 id="projects-title">Selected projects</h2></div>
            <p className="section-intro">Building systems from the storage layer up.</p>
            <div className="project-grid">
              {projects.map(project => (
                <article className="project" key={project.name}>
                  <p className="eyebrow">{project.category}</p><h3>{project.name}</h3>
                  <p className="body-copy">{project.summary}</p>
                  <ul className="project-highlights">{project.highlights.map(item => <li key={item}>{item}</li>)}</ul>
                  <ul className="tags" aria-label={`${project.name} topics`}>{project.tags.map(tag => <li key={tag}>{tag}</li>)}</ul>
                </article>
              ))}
            </div>
          </section>
          <section className="section page-width about-section" id="about" aria-labelledby="about-title">
            <div className="section-heading"><span className="section-number">03</span><h2 id="about-title">A little more about me</h2></div>
            <div className="about-grid">
              <div>
                <h3 className="subheading">Education</h3>
                {education.map(item => <article className="education-item" key={item.institution}><p className="date-label">{item.period}</p><h4>{item.institution}</h4><p>{item.studyType}</p></article>)}
                <h3 className="subheading tools-heading">Tools I work with</h3>
                <ul className="skills" aria-label="Technical skills">{skills.map(skill => <li key={skill}>{skill}</li>)}</ul>
              </div>
              <div>
                <h3 className="subheading">Teaching</h3>
                <p className="teaching-context">Undergraduate Learning Assistant<br />University of California, Santa Barbara</p>
                {teaching.map(item => <article className="teaching-item" key={item.course}><div><h4>{item.course}</h4><span className="date-label">{item.period}</span></div><p className="muted">{item.summary}</p></article>)}
              </div>
            </div>
          </section>
          <footer className="site-footer page-width" id="contact">
            <div><h2>Let's connect.</h2><p>Reach me by email, or find me on LinkedIn and GitHub.</p><ContactLinks /></div>
            <div className="footer-details"><p>© {new Date().getFullYear()} {basics.name}</p><p>Based on <a href="https://github.com/JoHoop/personal-website-react" target="_blank" rel="noopener noreferrer">JoHoop's template</a>.</p><a href="#top">Back to top ↑</a></div>
          </footer>
        </div>
      </main>
    </div>
  );
};
